import { useFormContext } from "react-hook-form";
import { useContext, useEffect } from "react";
import { GeaoContext } from '../../../GeaoContextProvider';
import AddressFields from "../../../../AddressFields";
import { AddressLabelsWithRequiredIndicator } from "../../../../../constants/AddressFieldLabels";

/**
 * AgencyAddresses component handles both physical and mailing address sections
 * including the synchronization logic between different address types.
 */
export default function AgencyAddresses() {
    const { register, watch, setValue } = useFormContext();
    const { agentInterview } = useContext(GeaoContext);

    // Watch address-related fields
    const watchPhysicalSameAsPrincipalResidential = watch("physicalSameAsPrincipalResidential");
    const watchMailingSameAsPrimaryPhysical = watch("mailingSameAsPrimaryPhysical");
    const watchPhysicalAddressLine1 = watch("physicalAddressLine1");
    const watchPhysicalAddressLine2 = watch("physicalAddressLine2");
    const watchPhysicalAddressCity = watch("physicalAddressCity");
    const watchPhysicalAddressState = watch("physicalAddressState");
    const watchPhysicalAddressZipCode = watch("physicalAddressZipCode");

    // Effect for syncing physical address with principal residential address
    useEffect(() => {
        if (watchPhysicalSameAsPrincipalResidential && agentInterview?.questionAnswers) {
            setValue("physicalAddressLine1", agentInterview?.questionAnswers?.ResidentialAddressStreetLineOne?.answer);
            setValue("physicalAddressLine2", agentInterview?.questionAnswers?.ResidentialAddressStreetLineTwo?.answer);
            setValue("physicalAddressZipCode", agentInterview?.questionAnswers?.ResidentialAddressZipCode?.answer);
            setValue("physicalAddressCity", agentInterview?.questionAnswers?.ResidentialAddressCity?.answer);
            setValue("physicalAddressState", agentInterview?.questionAnswers?.ResidentialAddressState?.answer);
        }
    }, [watchPhysicalSameAsPrincipalResidential, agentInterview?.questionAnswers, setValue]);

    // Effect for syncing mailing address with physical address
    useEffect(() => {
        if (watchMailingSameAsPrimaryPhysical) {
            setValue("mailingAddressLine1", watchPhysicalAddressLine1);
            setValue("mailingAddressLine2", watchPhysicalAddressLine2);
            setValue("mailingAddressZipCode", watchPhysicalAddressZipCode);
            setValue("mailingAddressCity", watchPhysicalAddressCity);
            setValue("mailingAddressState", watchPhysicalAddressState);
        }
    }, [
        watchMailingSameAsPrimaryPhysical,
        watchPhysicalAddressLine1,
        watchPhysicalAddressLine2,
        watchPhysicalAddressZipCode,
        watchPhysicalAddressCity,
        watchPhysicalAddressState,
        setValue
    ]);

    return (
        <>
            {/* Physical Address Section */}
            <h3 className="margin-top margin-bottom">Agency Primary Physical Address</h3>

            <fieldset className="form-field checkbox-group">
                <div className="checkbox-wrapper col-1">
                    <div className="label--description">
                        <input
                            id="same-as-residential-address"
                            type="checkbox"
                            {...register("physicalSameAsPrincipalResidential")}
                        />
                        <label htmlFor="same-as-residential-address" className="checkbox">
                            <span>Same as Principal Residential Address</span>
                        </label>
                    </div>
                </div>
            </fieldset>

            {!watchPhysicalSameAsPrincipalResidential &&
                <AddressFields
                    fieldNames={{
                        line1: "physicalAddressLine1",
                        line2: "physicalAddressLine2",
                        city: "physicalAddressCity",
                        state: "physicalAddressState",
                        zipCode: "physicalAddressZipCode"
                    }}
                    labels={AddressLabelsWithRequiredIndicator}
                />
            }

            {/* Mailing Address Section */}
            <h3 className="margin-top margin-bottom">Agency Primary Mailing Address</h3>

            <fieldset className="form-field checkbox-group">
                <div className="checkbox-wrapper col-1">
                    <div className="label--description">
                        <input
                            id="mailing-same-as-primary-physical"
                            type="checkbox"
                            {...register("mailingSameAsPrimaryPhysical")}
                        />
                        <label htmlFor="mailing-same-as-primary-physical" className="checkbox">
                            <span>Same as Agency Primary Physical Address</span>
                        </label>
                    </div>
                </div>
            </fieldset>

            {!watchMailingSameAsPrimaryPhysical &&
                <AddressFields
                    fieldNames={{
                        line1: "mailingAddressLine1",
                        line2: "mailingAddressLine2",
                        city: "mailingAddressCity",
                        state: "mailingAddressState",
                        zipCode: "mailingAddressZipCode"
                    }}
                    labels={AddressLabelsWithRequiredIndicator}
                />
            }
        </>
    );
}